import { Sword, Shield } from 'lucide-react';
import { t } from '../../localization/t';

const STAT_PILL_HEIGHT = '2.5cqw';
const PILL_GAP = '0.375cqw';

// Match the in-canvas card HUD hues: amber sword for ATK, red shield
// for DEF. Colour lives only in the icon so the pill itself stays on
// the dark medieval surface used elsewhere in the app.
const STAT_COLORS = {
  atk: '#f59e0b',
  def: '#ef4444',
};

function statPillStyle() {
  return {
    height: STAT_PILL_HEIGHT,
    minWidth: '4cqw',
    padding: '0 0.75cqw',
    borderRadius: 'var(--radius-xl)',
    border: '1px solid rgba(180,140,60,0.45)',
    background: 'rgba(12, 10, 8, 0.92)',
    color: '#e8d5a0',
    boxShadow: '0 6px 18px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.05)',
    textShadow: '0 1px 2px rgba(0,0,0,0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.375cqw',
    flex: '0 0 auto',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
  };
}

function formatDelta(n) {
  return n > 0 ? `+${n}` : `${n}`;
}

function StatPill({ Icon, delta, color, title }) {
  return (
    <div style={statPillStyle()} title={title}>
      <Icon style={{ width: '1.125cqw', height: '1.125cqw' }} strokeWidth={2.25} color={color} aria-hidden="true" />
      <span
        style={{
          fontSize: '1.125cqw',
          fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1,
          color: '#e8d5a0',
        }}
      >
        {formatDelta(delta)}
      </span>
    </div>
  );
}

/**
 * Centered above-the-card overlay for non-zero ATK / DEF modifiers.
 * Position is mutated each frame by updateHoverStatPillsPosition() in
 * GameBoard so the row tracks the card's top edge as the camera pans.
 *
 * Renders nothing when both deltas are zero/null. The overlay is a
 * "buff watch" indicator, not a stat readout. The unbuffed printed
 * stats live on the in-canvas card HUD already.
 */
export default function HoverStatPills({ hoverRef, attackDelta, defenseDelta }) {
  const hasAtk = attackDelta != null && attackDelta !== 0;
  const hasDef = defenseDelta != null && defenseDelta !== 0;
  const visible = hasAtk || hasDef;
  return (
    <div
      ref={hoverRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        display: visible ? 'flex' : 'none',
        gap: PILL_GAP,
        pointerEvents: 'none',
        zIndex: 49,
        left: 0,
        top: 0,
        // Anchor the pill row's bottom-center at the (left, top) origin
        // UpdateHoverStatPillsPosition translates the row to the
        // card's top-center, and this offset lifts it to sit just
        // above the card's top edge.
        transform: 'translate(-50%, -100%)',
        willChange: 'transform',
      }}
    >
      {hasAtk && <StatPill Icon={Sword} delta={attackDelta} color={STAT_COLORS.atk} title={t('Attack modifier')} />}
      {hasDef && <StatPill Icon={Shield} delta={defenseDelta} color={STAT_COLORS.def} title={t('Defense modifier')} />}
    </div>
  );
}
